function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="font-heading text-deep-blue text-xl font-semibold border-b border-[var(--gold)]/30 pb-2">
        {title}
      </h2>
      <div className="space-y-3 text-earth-brown/85 leading-relaxed">{children}</div>
    </div>
  )
}

function Bullets({ items }) {
  return (
    <ul className="list-disc list-outside ml-5 space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

export default function HowItWorks() {
  return (
    <section className="parchment-bg min-h-screen pt-28 pb-20 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-heading text-deep-blue text-3xl md:text-4xl font-bold mb-2">
          How Illuminate Works
        </h1>
        <p className="text-earth-brown/60 text-sm mb-10">
          How we handle theology, what the AI does and doesn&rsquo;t do, and what happens to your
          data. Plain language, no legalese.
        </p>

        <div className="bg-soft-white rounded-xl shadow-md p-8 md:p-12 space-y-8">
          <Section title="1. Our theological commitment">
            <p>
              Illuminate Faith is built around the historic Christian faith — the convictions
              Christians have held in common since the earliest centuries of the Church.
            </p>
            <p>
              The AI&rsquo;s primary alignment is with the Apostles&rsquo; Creed: one God in three
              Persons, the deity and bodily resurrection of Jesus Christ, salvation by grace through
              faith, the Church universal, and the future bodily resurrection of believers.
            </p>
            <p>
              Beyond that creedal core, Christians genuinely disagree on secondary matters —
              baptism, end times, spiritual gifts, Calvinist vs. Arminian, sacramental theology,
              women in ministry, age of the earth, and more. On those, Illuminate is
              denominationally neutral. It will explain different positions fairly when asked. It
              will not push you toward one tradition or away from another.
            </p>
            <p>
              Scripture is the final authority. Every response is grounded there first — not in
              tradition, not in the AI&rsquo;s training data, not in the loudest cultural voice.
            </p>
          </Section>

          <Section title="2. The AI is a tool, not a teacher">
            <p className="font-medium text-deep-blue/80 bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-lg px-4 py-3">
              Read this section even if you skip the rest.
            </p>
            <p>
              The AI on this site is a study aid. It is not a pastor. It is not the Holy Spirit. It
              is not a substitute for your local church.
            </p>
            <p>
              Use it to explore Scripture, prepare for conversations about faith, organize your
              thoughts, draft a sermon, or think through a question you have. Verify what it tells
              you against trusted sources — your pastor, scholars you respect, reliable
              commentaries, and Scripture itself.
            </p>
            <p>
              If the AI says something that contradicts what your trusted teachers say or what
              Scripture plainly teaches, trust them, not the AI. The AI gets things wrong.
              It&rsquo;s a calculator for faith study, not a prophet.
            </p>
            <p>Illuminate is meant to supplement your spiritual life, never replace it.</p>
          </Section>

          <Section title="3. How disagreements are surfaced">
            <p>
              When the AI discusses a topic Christians genuinely debate, it tags the response so you
              see a small &ldquo;Christians hold different views on this&rdquo; badge above the
              answer.
            </p>
            <p>Examples of tagged topics:</p>
            <Bullets
              items={[
                'Baptism mode — immersion, pouring, sprinkling',
                'End times — pre-trib, post-trib, amillennial',
                'Calvinism vs. Arminianism — sovereignty and free will',
                'Spiritual gifts — cessationist vs. continuationist',
                'Women in ministry, sacramental theology, age of the earth, alcohol, and others',
              ]}
            />
            <p>
              The badge doesn&rsquo;t replace the response — it just adds transparency. The AI will
              explain different positions; the badge tells you the explanation reflects multiple
              faithful Christian views, not just one tradition.
            </p>
            <p>
              This is intentional. Illuminate isn&rsquo;t here to tell you which position is right.
              That&rsquo;s between you, your pastor, your study, and the Holy Spirit.
            </p>
          </Section>

          <Section title="4. What the AI refuses to do">
            <p>Some things are off the table by design:</p>
            <Bullets
              items={[
                'Political endorsements. No candidates, no parties, no partisan issues. Scripture speaks to wisdom, justice, and human dignity — but the AI will not tell you who to vote for.',
                'Personal prophecy. It will not claim to give you a "word from the Lord" or predict your future.',
                'Mental-health replacement. For self-harm, suicidal thoughts, or any active mental-health crisis, the AI immediately routes you to professional resources (988 Suicide & Crisis Lifeline; Crisis Text Line at 741741). It does not try to counsel you through it.',
                'Made-up Scripture or scholar quotes. If the AI is unsure of the exact wording of a verse or whether someone said something, it says so rather than fabricate.',
              ]}
            />
          </Section>

          <Section title="5. Your data and the Claude API">
            <p>
              Illuminate Faith uses Anthropic&rsquo;s Claude API for AI conversations, Bible
              Insight, prayer generation, and study tools.
            </p>
            <p className="font-medium text-deep-blue/80">
              Anthropic does not train its models on your inputs.
            </p>
            <p>
              This is Anthropic&rsquo;s published API data-handling policy — not just a promise from
              us. Your prayers, chat messages, and anything else you share with the AI are not used
              to train Claude or any other model.
            </p>
            <p>
              We store your conversations and notes in our database (Supabase, encrypted). They are
              private to your account. We don&rsquo;t sell, share, or trade your data.
            </p>
            <p>
              You can delete your account and all associated data at any time from your account
              page. Deletion is permanent and complete.
            </p>
            <p>
              Full retention details and the rest of your data rights are in our{' '}
              <a href="/privacy" className="text-[var(--gold)] hover:underline">
                Privacy Policy
              </a>
              .
            </p>
          </Section>

          <Section title="6. Bug reports + how to reach me">
            <p>
              I&rsquo;m Mason — Illuminate Faith is built and maintained by one person. That means
              every email lands in my inbox, and I read all of them.
            </p>
            <p>
              If you find a bug, want to suggest a feature, disagree with how the AI handled
              something, or just want to say hi:{' '}
              <a
                href="mailto:hello@illuminatefaithai.com"
                className="text-[var(--gold)] hover:underline"
              >
                hello@illuminatefaithai.com
              </a>
              .
            </p>
            <p>Reply turnaround is usually a day or two. Sometimes faster.</p>
          </Section>

          <div className="pt-4 text-earth-brown/60 text-sm border-t border-[var(--gold)]/20">
            <p>
              For full legal terms, see our{' '}
              <a href="/terms" className="text-[var(--gold)] hover:underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy" className="text-[var(--gold)] hover:underline">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
